/**
 * Secure storage utility for sensitive credentials with encryption and freshness validation
 */

import { encryptData, decryptData, getUserEncryptionSeed } from './crypto';

export interface CouchCreds {
   url: string;
   username?: string;
   password?: string;
}

export interface CouchCredsWithMetadata extends CouchCreds {
   timestamp: number;
   version: string;
}

const STORAGE_VERSION = '1.0';
const CRED_FRESHNESS_HOURS = 24;

/**
 * Generates the localStorage key for CouchDB credentials
 */
const couchCredsKey = (userId: string) => `couchCreds:${userId}`;

/**
 * Checks if credentials are fresh (within the freshness window)
 * @param creds - Credentials with metadata
 * @returns true if credentials are fresh, false otherwise
 */
export function areCredentialsFresh(creds: CouchCredsWithMetadata): boolean {
   if (!creds.timestamp) return false;
   const hoursSinceUpdate = (Date.now() - creds.timestamp) / (1000 * 60 * 60);
   return hoursSinceUpdate < CRED_FRESHNESS_HOURS;
}

/**
 * Persists CouchDB credentials to encrypted localStorage
 * @param userId - The user's UID
 * @param creds - The CouchDB credentials to store
 */
export async function persistCouchCreds(
   userId: string,
   creds: CouchCreds,
): Promise<void> {
   if (typeof window === 'undefined') return;

   try {
      const credsWithMetadata: CouchCredsWithMetadata = {
         ...creds,
         timestamp: Date.now(),
         version: STORAGE_VERSION,
      };

      const seed = getUserEncryptionSeed(userId);
      const encrypted = await encryptData(JSON.stringify(credsWithMetadata), seed);
      localStorage.setItem(couchCredsKey(userId), encrypted);
   } catch (err) {
      console.error('Failed to persist CouchDB credentials', err);
      throw new Error('Could not securely store credentials');
   }
}

/**
 * Reads and decrypts CouchDB credentials from localStorage
 * @param userId - The user's UID
 * @returns The decrypted credentials with metadata, or null if not found/invalid
 */
export async function readCouchCreds(userId: string): Promise<CouchCredsWithMetadata | null> {
   if (typeof window === 'undefined') return null;

   try {
      const encrypted = localStorage.getItem(couchCredsKey(userId));
      if (!encrypted) return null;

      const seed = getUserEncryptionSeed(userId);
      const decrypted = await decryptData(encrypted, seed);
      const parsed = JSON.parse(decrypted) as CouchCredsWithMetadata;

      // Validate required fields
      if (!parsed.url || !parsed.username || !parsed.password) {
         console.warn('Stored credentials missing required fields');
         return null;
      }

      return parsed;
   } catch (err) {
      console.error('Failed to read CouchDB credentials', err);
      // Clear corrupted data
      localStorage.removeItem(couchCredsKey(userId));
      return null;
   }
}

/**
 * Clears CouchDB credentials from localStorage
 * @param userId - The user's UID
 */
export function clearCouchCreds(userId: string): void {
   if (typeof window === 'undefined') return;
   try {
      localStorage.removeItem(couchCredsKey(userId));
   } catch (err) {
      console.warn('Failed to clear CouchDB credentials', err);
   }
}

/**
 * Legacy function to read unencrypted credentials (for migration)
 * @deprecated Use readCouchCreds instead
 */
export function readLegacyCouchCreds(userId: string): CouchCreds | null {
   if (typeof window === 'undefined') return null;
   try {
      const raw = localStorage.getItem(couchCredsKey(userId));
      if (!raw) return null;

      // Try to parse as plain JSON (legacy format)
      const parsed = JSON.parse(raw);
      if (parsed?.url && parsed?.username && parsed?.password) {
         // Check if it's already encrypted (will fail JSON.parse if so)
         return parsed;
      }
   } catch (err) {
      // If JSON.parse fails, it's likely encrypted - return null
      return null;
   }
   return null;
}

/**
 * Migrates legacy unencrypted credentials to encrypted format
 * @param userId - The user's UID
 */
export async function migrateLegacyCredentials(userId: string): Promise<boolean> {
   const legacy = readLegacyCouchCreds(userId);
   if (!legacy) return false;

   try {
      await persistCouchCreds(userId, legacy);
      console.log('Successfully migrated legacy credentials to encrypted format');
      return true;
   } catch (err) {
      console.error('Failed to migrate legacy credentials', err);
      return false;
   }
}
